import { format, formatDistanceToNowStrict, isValid, isThisYear, parseISO } from 'date-fns';

import type {
  StorefrontImageStyle,
  StorefrontReviewStatus,
  StorefrontStatus,
  StorefrontTemplate,
} from '@/lib/api/storefrontAdminHooks';

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

/** Storefront root domain (`{slug}.{root}`), the same setting as system-web's
 *  NEXT_PUBLIC_STORE_ROOT_DOMAIN. A bare hostname; anything else falls back
 *  to the default. */
export const STORE_ROOT_DOMAIN = (() => {
  const raw = (import.meta.env.VITE_STORE_ROOT_DOMAIN ?? '').trim().toLowerCase();
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(raw) ? raw : 'rx-store.com';
})();

export function storeUrl(slug: string): string {
  return `https://${slug}.${STORE_ROOT_DOMAIN}`;
}

const PLAIN_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,119}$/;

/** Product artwork candidates on the storefront root domain (system-web's
 *  public/rxstore): the store's image style, then clinical-white, then the
 *  pre-style flat path. Only plain ids reach a URL path. */
export function productArtUrls(
  productId: string | null | undefined,
  style: string | null | undefined,
): string[] {
  if (!productId || !PLAIN_ID_RE.test(productId)) return [];
  const base = `https://${STORE_ROOT_DOMAIN}/rxstore/products`;
  const styles = [style && PLAIN_ID_RE.test(style) ? style : null, 'clinical-white'];
  return [
    ...[...new Set(styles.filter(Boolean))].map((s) => `${base}/${s}/${productId}.webp`),
    `${base}/${productId}.webp`,
  ];
}

/** A template image (`hero-1`, `lifestyle-2`, …) on the storefront root domain. */
export function templateImageUrl(
  template: string | null | undefined,
  imageId: string | null | undefined,
): string | null {
  if (!template || !imageId || !PLAIN_ID_RE.test(template) || !PLAIN_ID_RE.test(imageId)) {
    return null;
  }
  return `https://${STORE_ROOT_DOMAIN}/rxstore/templates/${template}/${imageId}.webp`;
}

/** Only https URLs (http on localhost) are opened or linked. */
export function safeHttpUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    if (url.protocol === 'https:' || (local && url.protocol === 'http:')) return url.href;
  } catch {
    /* not a URL */
  }
  return null;
}

export function usd(cents: number | null | undefined, opts: { signed?: boolean } = {}): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  const abs = Math.abs(cents) / 100;
  const body = abs.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: abs % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  if (cents < 0) return `−${body}`;
  return opts.signed && cents > 0 ? `+${body}` : body;
}

export function count(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : value.toLocaleString('en-US');
}

function parsed(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = parseISO(iso);
  return isValid(date) ? date : null;
}

export function shortDate(iso: string | null | undefined): string {
  const date = parsed(iso);
  return date ? format(date, 'MMM d, yyyy') : '—';
}

export function dateTime(iso: string | null | undefined): string {
  const date = parsed(iso);
  return date ? format(date, 'MMM d, yyyy · h:mm a') : '—';
}

/** "Oct 22, 3:04 PM" (the year only when it is not this year). */
export function shortDateTime(iso: string | null | undefined): string {
  const date = parsed(iso);
  if (!date) return '—';
  return format(date, isThisYear(date) ? 'MMM d, h:mm a' : 'MMM d, yyyy, h:mm a');
}

/** How long ago, compactly: "3h", "2d", "45m". */
export function ageLabel(iso: string | null | undefined): string | null {
  const date = parsed(iso);
  if (!date) return null;
  return formatDistanceToNowStrict(date, { roundingMethod: 'floor' })
    .replace(/ seconds?/, 's')
    .replace(/ minutes?/, 'm')
    .replace(/ hours?/, 'h')
    .replace(/ days?/, 'd')
    .replace(/ months?/, 'mo')
    .replace(/ years?/, 'y');
}

/** Review wait against the target: under a day is on time, a day or two
 *  is getting late, more is overdue. */
export function waitTone(iso: string | null | undefined): Tone {
  const date = parsed(iso);
  if (!date) return 'neutral';
  const hours = (Date.now() - date.getTime()) / 3_600_000;
  return hours >= 48 ? 'danger' : hours >= 24 ? 'warning' : 'success';
}

export function isAfter(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = parsed(a);
  const right = parsed(b);
  return Boolean(left && right && left.getTime() > right.getTime());
}

export const STATUS_LABEL: Record<StorefrontStatus, string> = {
  draft: 'Draft',
  live: 'Live',
  paused: 'Paused',
};

export const STATUS_TONE: Record<StorefrontStatus, Tone> = {
  draft: 'neutral',
  live: 'success',
  paused: 'warning',
};

export const REVIEW_LABEL: Record<StorefrontReviewStatus, string> = {
  draft: 'Not submitted',
  submitted: 'Awaiting review',
  changes_requested: 'Changes requested',
  changes_pending_review: 'Changes pending review',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const REVIEW_TONE: Record<StorefrontReviewStatus, Tone> = {
  draft: 'neutral',
  submitted: 'info',
  changes_requested: 'warning',
  changes_pending_review: 'info',
  approved: 'success',
  rejected: 'danger',
};

export const TEMPLATE_LABEL: Record<StorefrontTemplate, string> = {
  performance: 'Performance',
  renew: 'Renew',
  vitality: 'Vitality',
  clinical: 'Clinical',
};

export const IMAGE_STYLE_LABEL: Record<StorefrontImageStyle, string> = {
  'clinical-white': 'Clinical white',
  'amber-apothecary': 'Amber apothecary',
  'matte-black': 'Matte black',
  'tinted-glass': 'Tinted glass',
};

export function planLabel(plan: string, intervalMonths: number | null | undefined): string {
  const months =
    intervalMonths ??
    ({ monthly: 1, quarterly: 3, six_month: 6, twelve_month: 12 } as Record<string, number>)[
      plan
    ];
  if (months === 1) return 'Monthly';
  if (months) return `Every ${months} months`;
  return plan.replace(/_/g, ' ');
}

/** A real medical group name: one of the backend's allowed groups when it
 *  sends the list (`medical_group_options`); with an older backend, not
 *  empty and with no bracketed placeholder anywhere in it, such as
 *  "[Medical group name]" (the backend's go-live rule,
 *  `medical_group_ready`). */
export function isRealMedicalGroup(
  name: string | null | undefined,
  options?: readonly string[] | null,
): boolean {
  const value = (name ?? '').trim();
  if (options && options.length > 0) return canonicalMedicalGroup(value, options) !== null;
  return value.length > 0 && !/\[[^\]]*\]/.test(value);
}

/** The allow-list entry a name matches (any case), else null: the medical
 *  groups a store may name come from the backend (`medical_group_options`,
 *  `STOREFRONT_MEDICAL_GROUPS`). */
export function canonicalMedicalGroup(
  name: string | null | undefined,
  options: readonly string[],
): string | null {
  const wanted = (name ?? '').trim().toLowerCase();
  if (!wanted) return null;
  return options.find((option) => option.trim().toLowerCase() === wanted) ?? null;
}

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export function safeHex(value: string | null | undefined, fallback = '#111214'): string {
  return value && HEX_RE.test(value) ? value : fallback;
}

/** Text color that reads on a fill of `hex`. */
export function inkOn(hex: string): string {
  const h = safeHex(hex).slice(1);
  const full = h.length === 3 ? h.replace(/./g, (c) => c + c) : h;
  const n = Number.parseInt(full, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? '#0A0A0A' : '#FFFFFF';
}

/** The storefront site's monogram rule (system-web storefrontMonogram):
 *  the first letter or digit of each of the first two words ("Forge" is
 *  "F", "Iron Ritual" is "IR"). */
export function monogram(name: string | null | undefined): string {
  return (name ?? '')
    .trim()
    .split(/\s+/)
    .map((word) => word.match(/[\p{L}\p{N}]/u)?.[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/** Readable message for a backend refusal code. Fields the refusal
 *  carries (`ApiError.payload`, such as `balance_cents`) fill in the details. */
export function refusalMessage(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  const message = (error as { message?: string } | null)?.message;
  const extra = ((error as { payload?: Record<string, unknown> } | null)?.payload ?? {}) as Record<
    string,
    unknown
  >;
  const balance = typeof extra.balance_cents === 'number' ? extra.balance_cents : null;
  const known: Record<string, string> = {
    storefront_review_stale:
      'This store changed since you opened it, so nothing was saved. The changes are listed at the top of the page; look them over, then decide again.',
    storefront_payout_exceeds_balance:
      balance != null
        ? `That is more than the store is owed (${usd(balance)} after the payouts already marked paid). Lower the amount, or tick “Pay more than owed” and give a reason.`
        : 'That is more than the store is owed. Lower the amount, or tick “Pay more than owed” and give a reason.',
    storefront_payout_period_future:
      'A paid payout must cover a period that has ended. Set the period end to today or earlier, or record it as pending.',
    invite_used: 'This code was already used to create a store, so it cannot be revoked.',
    storefront_invite_not_found: 'That invite no longer exists. Reload the page.',
    storefront_medical_group_required:
      'Enter the medical group that reviews this store before it can go live.',
    storefront_has_no_products: 'The store has no active plan to sell yet.',
    storefront_price_below_floor: 'A plan is priced below its floor. The owner has to fix it.',
    storefront_plan_unavailable: 'A plan is for a term the catalog no longer prices.',
    storefront_pricing_unavailable: 'Pricing is unavailable right now (catalog unreachable). Try again.',
    storefront_product_unknown: 'A product is no longer in the catalog. The owner has to remove it.',
    storefront_review_note_required: 'Add a note for the owner.',
    storefront_review_note_too_long: 'The note is too long (2,000 characters at most).',
    storefront_branding_not_allowed:
      'Store owners or patients see this text, so it cannot name our own brand. Reword it.',
    storefront_review_status_invalid:
      'The store changed since this page loaded, or the action is not available for it. Reload and try again.',
    storefront_not_found: 'This store no longer exists.',
    storefront_status_invalid: 'That status change is not available for this store.',
    storefront_not_approved: 'Approve the store before it can go live again.',
    storefront_preview_unavailable: 'Previews are not configured on this server.',
    storefront_slug_reserved:
      'This store uses a reserved address, so it cannot be served, previewed or approved. Change the slug under Store settings first.',
    storefront_payout_amount_invalid: 'Enter an amount above $0 and at most $10,000,000.',
    storefront_payout_period_invalid: 'The period ends before it starts.',
    storefront_payout_paid_at_invalid: 'Enter when the payout was sent.',
    storefront_payout_not_found: 'That payout no longer exists. Reload the page.',
    storefront_slug_invalid:
      'Use 2 to 40 lowercase letters, digits or hyphens, starting with a letter or digit.',
    storefront_slug_taken: 'Another store already uses that address.',
    storefront_name_required: 'Enter a store name.',
    storefront_name_too_long: 'The store name is too long.',
    storefront_name_invalid: 'The store name has characters that are not allowed.',
    storefront_support_email_required: 'Enter a support email.',
    storefront_support_email_invalid: 'Enter a valid support email.',
    storefront_medical_group_name_too_long: 'The medical group name is too long.',
    storefront_claims_not_allowed:
      'This copy makes a medical or results claim, which store copy may not. Reword it.',
    storefront_rules_not_accepted:
      'The owner has not accepted the store rules yet, so the store cannot be approved. Request changes or wait for them to submit it.',
    storefront_not_submitted:
      'The owner has not submitted this store for review yet, so it cannot be approved.',
    storefront_medical_group_invalid:
      'Choose one of the medical groups we work with from the list.',
    storefront_revision_stale:
      'The change waiting for review was replaced or decided since you opened it, so nothing was saved. The page now shows the current change; look it over, then decide again.',
    storefront_revision_conflict:
      'The change waiting for review was replaced or decided since you opened it, so nothing was saved. Reload the page and decide again.',
    storefront_no_approved_version:
      'This store has no approved version to go back to yet.',
    storefront_no_changes: 'Nothing is waiting for review on this store.',
    storefront_publish_failed: 'The change could not be published. Try again.',
    storefront_preview_content_invalid: 'That preview is not available for this store.',
    storefront_draft_conflict:
      'The owner changed their draft at the same moment. Reload the page and try again.',
    storefront_review_policy_required:
      'Choose a review policy for this store: it decides which of the owner’s later changes wait for review.',
    storefront_review_policy_invalid: 'Choose one of the four review policies.',
    storefront_status_reason_required: 'Add a reason for the owner.',
    storefront_status_reason_too_long: 'The reason is too long (2,000 characters at most).',
    storefront_payout_void_reason_required: 'Add a reason the owner can read.',
    storefront_payout_void_reason_too_long: 'The reason is too long (500 characters at most).',
  };
  if (code && known[code]) return known[code];
  // An unmapped refusal: the backend's own sentence when it sent one (never
  // a bare code), else a plain fallback; the code stays in the tooltip-free
  // text only as a reference for support.
  const sentence =
    message && message !== code && !/^[a-z0-9_]+$/.test(message) ? message : null;
  if (code && code !== 'unknown_error') {
    return `${sentence ?? 'The server refused this action'}. Reload the page and try again. (Reference: ${code.replace(/_/g, ' ')})`.replace('.. ', '. ');
  }
  return sentence || 'Something went wrong. Try again.';
}
